import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-jalaali';

type DatePickerPlacement = 'below' | 'above' | 'auto';

interface PersianDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  required?: boolean;
  compact?: boolean;
  borderless?: boolean;
  /** below = زیر input (پیش‌فرض)، above = بالای input، auto = بر اساس فضای صفحه */
  placement?: DatePickerPlacement;
}

const PersianDatePicker: React.FC<PersianDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'تاریخ را انتخاب کنید',
  required = false,
  compact = false,
  borderless = false,
  placement = 'below',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);
  const [currentMonth, setCurrentMonth] = useState<moment.Moment>(moment());
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left: number;
    width: number;
    openUp: boolean;
  } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const parts = value.split('/');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const m = moment().jYear(year).jMonth(month).jDate(day);
        if (m.isValid()) {
          setSelectedDate(m);
          setCurrentMonth(m.clone());
        }
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const updatePanelPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 340;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let openUp = placement === 'above';
    if (placement === 'auto') {
      openUp = spaceBelow < panelHeight + gap && spaceAbove > spaceBelow;
    }

    setPanelPos({
      top: openUp ? rect.top - gap : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, [placement]);

  useEffect(() => {
    if (!isOpen) {
      setPanelPos(null);
      return;
    }

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDateSelect = (date: moment.Moment) => {
    setSelectedDate(date);
    onChange(date.format('jYYYY/jMM/jDD'));
    setIsOpen(false);
  };

  const getDaysInMonth = () => {
    const days: moment.Moment[] = [];
    const firstDay = currentMonth.clone().startOf('jMonth');
    const lastDay = currentMonth.clone().endOf('jMonth');
    const daysInMonth = lastDay.jDate();

    for (let i = 1; i <= daysInMonth; i += 1) {
      days.push(firstDay.clone().jDate(i));
    }

    return days;
  };

  const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
  const monthNames = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند',
  ];

  const days = getDaysInMonth();
  const firstDay = currentMonth.clone().startOf('jMonth');
  const dayOfWeek = firstDay.day();
  const offset = (dayOfWeek + 1) % 7;
  const displayValue = selectedDate ? selectedDate.format('jYYYY/jMM/jDD') : '';

  const calendarPanel =
    isOpen &&
    panelPos &&
    createPortal(
      <div
        ref={panelRef}
        className="fixed z-[9998] rounded-lg border border-gray-300 bg-white p-4 shadow-xl"
        style={{
          direction: 'rtl',
          top: panelPos.top,
          left: panelPos.left,
          minWidth: Math.max(panelPos.width, 260),
          transform: panelPos.openUp ? 'translateY(-100%)' : undefined,
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'jMonth'))}
            className="rounded p-1 hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="text-lg font-semibold">
            {monthNames[currentMonth.jMonth()]} {currentMonth.jYear()}
          </div>
          <button
            type="button"
            onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'jMonth'))}
            className="rounded p-1 hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <div className="mb-2 grid grid-cols-7 gap-1">
          {weekDays.map((day) => (
            <div key={day} className="py-1 text-center text-sm font-medium text-gray-600">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: offset }).map((_, index) => (
            <div key={`empty-${index}`} className="h-8" />
          ))}

          {days.map((day) => {
            const isSelected = selectedDate && day.isSame(selectedDate, 'day');
            const isToday = day.isSame(moment(), 'day');

            return (
              <button
                key={day.format('jYYYY-jMM-jDD')}
                type="button"
                onClick={() => handleDateSelect(day)}
                className={`h-9 w-9 rounded text-base [font-variant-numeric:tabular-nums] transition-colors hover:bg-blue-100 ${
                  isSelected
                    ? 'bg-blue-600 text-white'
                    : isToday
                      ? 'bg-blue-50 font-semibold text-blue-600'
                      : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                {day.jDate()}
              </button>
            );
          })}
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => handleDateSelect(moment())}
            className="w-full rounded-md px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
          >
            امروز
          </button>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="relative" ref={wrapperRef} style={{ direction: 'rtl' }}>
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        readOnly
        onClick={() => {
          setIsOpen((open) => !open);
          if (!isOpen) {
            requestAnimationFrame(updatePanelPosition);
          }
        }}
        placeholder={placeholder}
        required={required}
        className={`w-full cursor-pointer text-center font-semibold tracking-wide text-gray-900 [direction:ltr] [font-variant-numeric:tabular-nums] focus:outline-none ${
          compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2.5 text-base'
        } ${
          borderless
            ? 'border-0 bg-transparent focus:ring-0'
            : 'rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500'
        }`}
      />
      {calendarPanel}
    </div>
  );
};

export default PersianDatePicker;
